import { Globe, ScanLine, X } from 'lucide-react'
import { type FC, useCallback, useEffect, useState } from 'react'
import { Button, Input, Modal, NumberInput, Spinner, TRPCError } from '~/components/ui'
import { MacroInput } from '~/features/ingredients'
import type { OFFProduct } from '~/lib'
import { type RouterOutput, trpc } from '~/lib/trpc'
import { BarcodeLookup } from './BarcodeLookup'

type PremadeRecipe = NonNullable<RouterOutput['recipe']['addPremade']>

export interface PremadeDialogProps {
	open: boolean
	onClose: () => void
	onCreated?: (recipe: PremadeRecipe) => void
}

export const PremadeDialog: FC<PremadeDialogProps> = ({ open, onClose, onCreated }) => {
	const [name, setName] = useState('')
	const [url, setUrl] = useState('')
	const [servingSize, setServingSize] = useState('')
	const [servings, setServings] = useState('1')
	const [protein, setProtein] = useState('')
	const [carbs, setCarbs] = useState('')
	const [fat, setFat] = useState('')
	const [kcal, setKcal] = useState('')
	const [fiber, setFiber] = useState('')
	const [barcodeActive, setBarcodeActive] = useState(false)

	const utils = trpc.useUtils()

	const parseProduct = trpc.ai.parseProduct.useMutation({
		onSuccess: data => {
			setName(data.name)
			setServingSize(String(data.servingSize))
			if (data.servings != null) setServings(String(data.servings))
			setProtein(String(data.protein))
			setCarbs(String(data.carbs))
			setFat(String(data.fat))
			setKcal(String(data.kcal))
			setFiber(String(data.fiber))
		}
	})

	const addPremade = trpc.recipe.addPremade.useMutation({
		onSuccess: recipe => {
			if (!recipe) return
			utils.recipe.list.invalidate()
			onCreated?.(recipe)
			onClose()
		}
	})

	// Reset state when dialog closes
	const resetAdd = addPremade.reset
	const resetParse = parseProduct.reset
	useEffect(() => {
		if (!open) {
			setName('')
			setUrl('')
			setServingSize('')
			setServings('1')
			setProtein('')
			setCarbs('')
			setFat('')
			setKcal('')
			setFiber('')
			setBarcodeActive(false)
			resetAdd()
			resetParse()
		}
	}, [open, resetAdd, resetParse])

	// Close on Escape
	useEffect(() => {
		if (!open) return
		const handler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', handler)
		return () => document.removeEventListener('keydown', handler)
	}, [open, onClose])

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		const sz = Number.parseFloat(servingSize)
		const sv = Number.parseFloat(servings) || 1
		if (!(name.trim() && sz)) return

		addPremade.mutate({
			name: name.trim(),
			servingSize: sz,
			servings: sv,
			protein: Number.parseFloat(protein) || 0,
			carbs: Number.parseFloat(carbs) || 0,
			fat: Number.parseFloat(fat) || 0,
			kcal: Number.parseFloat(kcal) || 0,
			fiber: Number.parseFloat(fiber) || 0,
			sourceUrl: url.trim() || null
		})
	}

	const handleBarcodeProduct = useCallback((product: OFFProduct) => {
		setName(product.name)
		setServingSize(String(product.servingSize))
		if (product.servings != null) setServings(String(product.servings))
		setProtein(String(product.protein))
		setCarbs(String(product.carbs))
		setFat(String(product.fat))
		setKcal(String(product.kcal))
		setFiber(String(product.fiber))
		setUrl(`https://world.openfoodfacts.org/product/${product.barcode}`)
	}, [])

	const canSubmit = name.trim() && Number.parseFloat(servingSize) > 0

	if (!open) return null

	return (
		<Modal className="w-full max-w-sm">
			{/* Header */}
			<div className="flex items-center justify-between border-edge border-b px-4 py-3">
				<h2 className="font-semibold text-ink">Add Premade</h2>
				<Button variant="ghost" size="icon" onClick={onClose} disabled={addPremade.isPending}>
					<X className="size-4" />
				</Button>
			</div>

			{/* Content */}
			<form onSubmit={handleSubmit} className="space-y-3 p-4">
				{barcodeActive ? (
					<BarcodeLookup
						active
						onProductFound={p => {
							handleBarcodeProduct(p)
							setBarcodeActive(false)
						}}
						onClose={() => setBarcodeActive(false)}
					/>
				) : (
					<Button type="button" variant="outline" className="w-full" onClick={() => setBarcodeActive(true)}>
						<ScanLine className="size-4" />
						Scan barcode
					</Button>
				)}

				<div className="flex items-center gap-3">
					<div className="h-px flex-1 bg-edge" />
					<span className="text-ink-faint text-xs">or fetch from URL</span>
					<div className="h-px flex-1 bg-edge" />
				</div>

				<div className="flex gap-2">
					<Input
						placeholder="https://example.com/product..."
						value={url}
						onChange={e => setUrl(e.target.value)}
						className="flex-1"
					/>
					<Button
						type="button"
						variant="outline"
						disabled={!url.trim() || parseProduct.isPending}
						onClick={() => parseProduct.mutate({ url: url.trim() })}
					>
						{parseProduct.isPending ? (
							<Spinner className="size-4 text-current" />
						) : (
							<Globe className="size-4" />
						)}
						Fetch
					</Button>
				</div>

				{parseProduct.error && <TRPCError error={parseProduct.error} />}

				<div className="flex items-center gap-3">
					<div className="h-px flex-1 bg-edge" />
					<span className="text-ink-faint text-xs">or enter manually</span>
					<div className="h-px flex-1 bg-edge" />
				</div>

				<Input placeholder="Product name" value={name} onChange={e => setName(e.target.value)} />

				<div className="grid grid-cols-2 gap-2">
					<label>
						<span className="mb-1 block text-ink-muted text-xs">Serving size (g)</span>
						<NumberInput
							value={servingSize}
							onChange={e => setServingSize(e.target.value)}
							placeholder="e.g. 60"
						/>
					</label>
					<label>
						<span className="mb-1 block text-ink-muted text-xs">Servings</span>
						<NumberInput value={servings} onChange={e => setServings(e.target.value)} placeholder="1" />
					</label>
				</div>

				<div className="space-y-1.5">
					<span className="text-ink-muted text-xs">Per serving</span>
					<div className="grid grid-cols-5 gap-2">
						<MacroInput label="Protein" value={protein} onChange={setProtein} />
						<MacroInput label="Carbs" value={carbs} onChange={setCarbs} />
						<MacroInput label="Fat" value={fat} onChange={setFat} />
						<MacroInput label="Kcal" value={kcal} onChange={setKcal} />
						<MacroInput label="Fiber" value={fiber} onChange={setFiber} />
					</div>
				</div>

				{addPremade.error && <TRPCError error={addPremade.error} />}

				<div className="flex justify-end gap-2 pt-1">
					<Button type="button" variant="ghost" onClick={onClose} disabled={addPremade.isPending}>
						Cancel
					</Button>
					<Button type="submit" disabled={!canSubmit || addPremade.isPending}>
						{addPremade.isPending ? (
							<>
								<Spinner className="size-4 text-current" />
								Adding...
							</>
						) : (
							'Add'
						)}
					</Button>
				</div>
			</form>
		</Modal>
	)
}
